import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

let M = "/module-rbac/hris";
const fmt = (d) => (d ? new Date(d).toLocaleString() : "\u2014");

const Chips = ({ items, onRemove, testid }) => (
  <div className="flex flex-wrap gap-1.5 min-h-[28px]" data-testid={testid}>
    {items.length === 0 && <span className="text-xs text-slate-400">None selected (no extra restriction)</span>}
    {items.map((it) => (
      <span key={it.id} className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-xs">
        {it.name}<button type="button" onClick={() => onRemove(it.id)} className="hover:text-blue-900"><Icons.X className="h-3 w-3" /></button>
      </span>
    ))}
  </div>
);

const MultiPick = ({ label, options, value, onChange, allLabel, testid, onAddNew }) => {
  const [pick, setPick] = useState("");
  const [newName, setNewName] = useState("");
  const chosen = options.filter((o) => value.includes(o.id));
  const left = options.filter((o) => !value.includes(o.id));
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2">
        <Select value={pick} onValueChange={(v) => { onChange([...value, v]); setPick(""); }}>
          <SelectTrigger className="h-9" data-testid={`${testid}-select`}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
          <SelectContent>{left.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">Nothing left to add</div> : left.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="button" variant="outline" size="sm" className="h-9 whitespace-nowrap" onClick={() => onChange(options.map((o) => o.id))} data-testid={`${testid}-all`}>{allLabel}</Button>
      </div>
      {onAddNew && (
        <div className="flex gap-2">
          <Input className="h-8 text-xs" placeholder={`New ${label.toLowerCase()} name`} value={newName} onChange={(e) => setNewName(e.target.value)} data-testid={`${testid}-new`} />
          <Button type="button" size="sm" variant="ghost" className="h-8" disabled={!newName.trim()} onClick={async () => { await onAddNew(newName.trim()); setNewName(""); }} data-testid={`${testid}-new-add`}><Icons.Plus className="h-3.5 w-3.5 mr-1" />Add to list</Button>
        </div>
      )}
      <Chips items={chosen} onRemove={(id) => onChange(value.filter((x) => x !== id))} testid={`${testid}-chips`} />
    </div>
  );
};

const EditDialog = ({ row, roles, companies, scopes, reloadScopes, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (row) {
      const byName = (kind, names) => scopes[kind].filter((s) => names.includes(s.name)).map((s) => s.id);
      setF({ role_id: row.role_id, status: row.status, company_ids: row.company_ids, location_ids: byName("location", row.location_names), project_ids: byName("project", row.project_names) });
    }
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!row || !f) return null;
  const addScope = (kind) => async (name) => {
    try { const { data } = await api.post(`${M}/scopes/${kind}`, { name }); await reloadScopes(); setF((x) => ({ ...x, [`${kind}_ids`]: [...x[`${kind}_ids`], data.id] })); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const save = async () => {
    const names = (kind, ids) => scopes[kind].filter((s) => ids.includes(s.id)).map((s) => s.name);
    try {
      await api.put(`${M}/users/${row.employee_id}`, { role_id: f.role_id, status: f.status, company_ids: f.company_ids, location_names: names("location", f.location_ids), project_names: names("project", f.project_ids) });
      toast.success("User access updated"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="hris-user-edit-dialog">
        <DialogHeader><DialogTitle>Edit User: {row.name}</DialogTitle><DialogDescription>Name and email come from the Employee Directory and can't be changed here.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Name</Label><Input value={row.name} disabled data-testid="hris-user-name" /></div>
          <div><Label className="text-xs">Email</Label><Input value={row.email || ""} disabled data-testid="hris-user-email" /></div>
          <div><Label className="text-xs">Role</Label>
            <Select value={f.role_id} onValueChange={(v) => setF({ ...f, role_id: v })}>
              <SelectTrigger data-testid="hris-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.id === "employee" ? `Employee (${row.rank})` : r.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Status</Label>
            <div className="flex items-center gap-2 h-10"><Switch checked={f.status === "Active"} onCheckedChange={(c) => setF({ ...f, status: c ? "Active" : "Inactive" })} data-testid="hris-user-status-switch" /><span className="text-sm">{f.status}</span></div></div>
        </div>
        <div className="space-y-4 pt-1">
          <MultiPick label="Company" allLabel="Add all company" testid="hris-user-company" options={companies} value={f.company_ids} onChange={(v) => setF({ ...f, company_ids: v })} />
          <MultiPick label="Location" allLabel="Add all location" testid="hris-user-location" options={scopes.location} value={f.location_ids} onChange={(v) => setF({ ...f, location_ids: v })} onAddNew={addScope("location")} />
          <MultiPick label="Project" allLabel="Add all project" testid="hris-user-project" options={scopes.project} value={f.project_ids} onChange={(v) => setF({ ...f, project_ids: v })} onAddNew={addScope("project")} />
        </div>
        <p className="text-[11px] text-slate-500">Company adds access to that company's HRIS records. The role's ticked permissions still decide what they can open. Location and Project limit the Employee Directory to employees whose Work Location and Project match.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="hris-user-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function HrisUsers({ mid = "hris" }) {
  M = `/module-rbac/${mid}`;
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [scopes, setScopes] = useState({ location: [], project: [] });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [applied, setApplied] = useState({ q: "", role: "all" });
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const size = 15;

  const reloadScopes = useCallback(async () => {
    const [l, p] = await Promise.all([api.get(`${M}/scopes/location`), api.get(`${M}/scopes/project`)]);
    setScopes({ location: l.data, project: p.data });
  }, []);
  const load = useCallback(async () => {
    const params = { q: applied.q, ...(applied.role !== "all" ? { role_id: applied.role } : {}) };
    const [u, r] = await Promise.all([api.get(`${M}/users`, { params }), api.get(`${M}/roles`)]);
    setRows(u.data); setRoles(r.data); setPage(1); setLoaded(true);
  }, [applied]);
  useEffect(() => { load().catch((e) => toast.error(e.response?.data?.detail || "Could not load users")); }, [load]);
  useEffect(() => { reloadScopes().catch(() => {}); api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {}); }, [reloadScopes]);

  const shown = useMemo(() => rows.slice((page - 1) * size, page * size), [rows, page]);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 grid gap-3 md:grid-cols-[2fr_1fr_auto] items-end" data-testid="hris-users-filters">
        <div><Label className="text-xs">Search Name / Biometric Code</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setApplied({ q, role })} placeholder="Name, employee no., biometric code or email" data-testid="hris-users-search" /></div>
        <div><Label className="text-xs">Choose Role</Label>
          <Select value={role} onValueChange={setRole}><SelectTrigger data-testid="hris-users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All roles</SelectItem>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex gap-2">
          <Button onClick={() => setApplied({ q, role })} data-testid="hris-users-filter-btn"><Icons.Filter className="h-4 w-4 mr-1.5" />Filter</Button>
          <Button variant="outline" onClick={() => { setQ(""); setRole("all"); setApplied({ q: "", role: "all" }); }} data-testid="hris-users-reset-btn">Reset Filter</Button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hris-users-table">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500"><tr>
              {["ID", "Name", "Email", "Company", "Role", "Status", "Last Modified At", "Action"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {shown.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400" data-testid="hris-users-empty">{loaded ? "No users match." : "Loading users…"}</td></tr>}
              {shown.map((r) => (
                <tr key={r.employee_id} className="hover:bg-slate-50/70" data-testid={`hris-user-row-${r.employee_id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{r.employee_no}{r.biometric_id && <div className="text-slate-400">{r.biometric_id}</div>}</td>
                  <td className="px-3 py-2.5 font-medium whitespace-nowrap">{r.name}{!r.has_login && <span className="ml-1.5 text-[10px] text-amber-600 border border-amber-200 bg-amber-50 rounded px-1">No login</span>}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.email}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.company}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs border ${r.role_id === "employee" ? "bg-slate-50 text-slate-600 border-slate-200" : "bg-violet-50 text-violet-700 border-violet-200"}`} data-testid={`hris-user-role-${r.employee_id}`}>{r.role_id === "employee" ? `Employee \u00b7 ${r.rank}` : r.role}</span></td>
                  <td className="px-3 py-2.5"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs border ${r.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{r.status}</span></td>
                  <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmt(r.updated_at)}{r.updated_by && <div>{r.updated_by}</div>}</td>
                  <td className="px-3 py-2.5"><Button size="sm" variant="outline" disabled={!r.has_login} onClick={() => setEditing(r)} data-testid={`hris-user-edit-${r.employee_id}`}><Icons.Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span data-testid="hris-users-count">{rows.length} user(s) from the Employee Directory</span>
          <div className="flex items-center gap-2"><Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="hris-users-prev">Previous</Button><span>{page} / {pages}</span><Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="hris-users-next">Next</Button></div>
        </div>
      </div>
      <EditDialog row={editing} roles={roles} companies={companies} scopes={scopes} reloadScopes={reloadScopes} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
